import type { ComputedRef, InjectionKey, MaybeRefOrGetter } from 'vue'
import { computed, inject, provide, toValue } from 'vue'

import en from '@/i18n/locales/en.json'
import zhCN from '@/i18n/locales/zh-CN.json'

export type ApiReferenceLocaleMessages = {
  [key: string]: string | ApiReferenceLocaleMessages
}

export type ApiReferenceI18nOptions = {
  locale?: string
  fallbackLocale?: string
  messages?: Record<string, ApiReferenceLocaleMessages>
}

export type ApiReferenceI18nParams = Record<
  string,
  string | number | boolean | null | undefined
>

type ApiReferenceI18n = {
  locale: string
  fallbackLocale: string
  messages: Record<string, ApiReferenceLocaleMessages>
  t: (key: string, params?: ApiReferenceI18nParams) => string
}

const DEFAULT_LOCALE = 'en'

export const builtInLocales = {
  en,
  'zh-CN': zhCN,
} satisfies Record<string, ApiReferenceLocaleMessages>

const builtInLocaleMap: Record<string, ApiReferenceLocaleMessages> = builtInLocales

const I18N_SYMBOL = Symbol() as InjectionKey<ComputedRef<ApiReferenceI18n>>

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const deepMergeMessages = (
  base: ApiReferenceLocaleMessages,
  override?: ApiReferenceLocaleMessages,
): ApiReferenceLocaleMessages => {
  if (!override) {
    return base
  }

  const merged: ApiReferenceLocaleMessages = { ...base }

  Object.entries(override).forEach(([key, value]) => {
    const current = merged[key]
    if (isObject(current) && isObject(value)) {
      merged[key] = deepMergeMessages(
        current as ApiReferenceLocaleMessages,
        value as ApiReferenceLocaleMessages,
      )
      return
    }

    merged[key] = value as string | ApiReferenceLocaleMessages
  })

  return merged
}

const getMessage = (
  messages: ApiReferenceLocaleMessages | undefined,
  key: string,
): string | undefined => {
  if (!messages) {
    return undefined
  }

  let current: string | ApiReferenceLocaleMessages | undefined = messages

  for (const part of key.split('.')) {
    if (!isObject(current) || !(part in current)) {
      return undefined
    }
    current = current[part] as string | ApiReferenceLocaleMessages
  }

  return typeof current === 'string' ? current : undefined
}

const interpolate = (message: string, params?: ApiReferenceI18nParams) => {
  if (!params) {
    return message
  }

  return message.replace(/\{(\w+)\}/g, (_match, token: string) => {
    const value = params[token]
    return value === undefined || value === null ? '' : String(value)
  })
}

const getLocaleCandidates = (locale: string) => {
  const candidates = [locale]
  const baseLocale = locale.split('-')[0]

  if (baseLocale && baseLocale !== locale) {
    candidates.push(baseLocale)
  }

  return candidates
}

export const createApiReferenceI18n = (
  options?: ApiReferenceI18nOptions,
): ApiReferenceI18n => {
  const locale = options?.locale ?? DEFAULT_LOCALE
  const fallbackLocale = options?.fallbackLocale ?? DEFAULT_LOCALE

  const localeKeys = new Set([
    ...Object.keys(builtInLocales),
    ...Object.keys(options?.messages ?? {}),
  ])

  const messages = Array.from(localeKeys).reduce<
    Record<string, ApiReferenceLocaleMessages>
  >((acc, localeKey) => {
    acc[localeKey] = deepMergeMessages(
      builtInLocaleMap[localeKey] ?? {},
      options?.messages?.[localeKey],
    )
    return acc
  }, {})

  const t = (key: string, params?: ApiReferenceI18nParams) => {
    const localesToTry = [
      ...getLocaleCandidates(locale),
      ...getLocaleCandidates(fallbackLocale),
    ]

    for (const localeKey of localesToTry) {
      const message = getMessage(messages[localeKey], key)
      if (message) {
        return interpolate(message, params)
      }
    }

    return key
  }

  return {
    locale,
    fallbackLocale,
    messages,
    t,
  }
}

export const provideApiReferenceI18n = (
  i18n: MaybeRefOrGetter<ApiReferenceI18nOptions | undefined>,
) => {
  const context = computed(() => createApiReferenceI18n(toValue(i18n)))
  provide(I18N_SYMBOL, context)
  return context
}

export const useI18n = () => {
  const context = inject(
    I18N_SYMBOL,
    computed(() => createApiReferenceI18n(undefined)),
  )

  return {
    t: (key: string, params?: ApiReferenceI18nParams) =>
      context.value.t(key, params),
    locale: computed(() => context.value.locale),
    fallbackLocale: computed(() => context.value.fallbackLocale),
  }
}
